import type { AlphaPattern, ColorIndex } from './types';
import { PatternError } from './normal';

/**
 * Simulation of an alpha (pixel-grid) pattern.
 *
 * Physically: `cols` base strings hang straight down and never move. One long
 * "leader" string travels horizontally across the row, tying one knot over each
 * base string in turn, then reverses and comes back for the next row.
 *
 * At each intersection exactly two threads are present: the base string of that
 * column and the leader. So a cell can only ever be one of those two colours:
 *
 *   - cell colour === leader colour  -> the LEADER wraps the base string.
 *     The leader is the working thread, so the leader's colour shows.
 *
 *   - cell colour === base colour    -> the BASE string wraps the leader.
 *     The base string is the working thread, so the base colour shows. The base
 *     string then drops back into its own column; it does not travel.
 *
 * That constraint is why alpha patterns are usually two-colour (a background
 * leader and a foreground base). Knotwright enforces it rather than drawing a
 * pretty grid that cannot actually be tied.
 */

export type AlphaKnotActor = 'leader' | 'base';

export interface AlphaKnot {
  row: number;
  /** Column of the base string being knotted. */
  col: number;
  /** Order in which this knot is tied within its row (0-based). */
  sequence: number;
  /** Which thread does the wrapping, and therefore which colour shows. */
  actor: AlphaKnotActor;
  color: ColorIndex;
  leaderColor: ColorIndex;
  baseColor: ColorIndex;
}

export interface AlphaRow {
  row: number;
  direction: 'ltr' | 'rtl';
  leaderColor: ColorIndex;
  /** Knots in the order they are physically tied. */
  knots: AlphaKnot[];
}

export interface AlphaSimulation {
  rows: AlphaRow[];
  cols: number;
  /** Knots tied by the leader thread (the thread that eats all the floss). */
  leaderKnots: number;
  /** Knots tied by each base string, indexed by column. */
  baseKnots: number[];
  totalKnots: number;
}

export function rowDirection(
  pattern: Pick<AlphaPattern, 'firstRowDirection'>,
  row: number,
): 'ltr' | 'rtl' {
  const flip = row % 2 === 1;
  const first = pattern.firstRowDirection;
  if (!flip) return first;
  return first === 'ltr' ? 'rtl' : 'ltr';
}

/**
 * Check that every cell can actually be tied. Returns a list of problems rather
 * than throwing, so the design generator can repair a grid instead of dying.
 */
export function validateAlpha(pattern: AlphaPattern): string[] {
  const problems: string[] = [];
  const { grid, baseColors, leaderColors } = pattern;
  const cols = baseColors.length;

  if (cols === 0) problems.push('An alpha pattern needs at least one base string.');
  if (grid.length !== leaderColors.length) {
    problems.push(
      `Grid has ${grid.length} rows but ${leaderColors.length} leader colours were given.`,
    );
  }

  for (let r = 0; r < grid.length; r++) {
    const row = grid[r]!;
    if (row.length !== cols) {
      problems.push(`Row ${r + 1} has ${row.length} cells but there are ${cols} base strings.`);
      continue;
    }
    const leader = leaderColors[r];
    for (let c = 0; c < cols; c++) {
      const cell = row[c]!;
      if (cell !== leader && cell !== baseColors[c]) {
        problems.push(
          `Row ${r + 1}, column ${c + 1} asks for colour ${cell}, but only the leader ` +
            `(${leader}) and that column's base string (${baseColors[c]}) are present there.`,
        );
      }
    }
  }
  return problems;
}

export function simulateAlpha(pattern: AlphaPattern): AlphaSimulation {
  const problems = validateAlpha(pattern);
  if (problems.length > 0) {
    throw new PatternError(
      `This alpha pattern cannot be tied as drawn:\n- ${problems.slice(0, 5).join('\n- ')}`,
    );
  }

  const cols = pattern.baseColors.length;
  const baseKnots = new Array<number>(cols).fill(0);
  let leaderKnots = 0;
  let totalKnots = 0;
  const rows: AlphaRow[] = [];

  for (let r = 0; r < pattern.grid.length; r++) {
    const direction = rowDirection(pattern, r);
    const leaderColor = pattern.leaderColors[r]!;
    const knots: AlphaKnot[] = [];

    // The leader physically walks the row, so knots are emitted in travel
    // order. A right-to-left row is tied starting from the last column.
    const columns =
      direction === 'ltr'
        ? Array.from({ length: cols }, (_, i) => i)
        : Array.from({ length: cols }, (_, i) => cols - 1 - i);

    columns.forEach((col, sequence) => {
      const cell = pattern.grid[r]![col]!;
      const baseColor = pattern.baseColors[col]!;
      // When the cell matches both (leader and base are the same colour there)
      // the leader does the work, because that is the thread already in hand.
      const actor: AlphaKnotActor = cell === leaderColor ? 'leader' : 'base';
      if (actor === 'leader') leaderKnots++;
      else baseKnots[col]! += 1;
      totalKnots++;
      knots.push({ row: r, col, sequence, actor, color: cell, leaderColor, baseColor });
    });

    rows.push({ row: r, direction, leaderColor, knots });
  }

  return { rows, cols, leaderKnots, baseKnots, totalKnots };
}

/**
 * Force an arbitrary picture into something an alpha bracelet can express.
 *
 * Strategy: pick the most common colour overall as the leader (background),
 * then give each column a base string equal to the most common *other* colour
 * in that column. Any cell that is still unrepresentable is snapped to whichever
 * of the two available threads is closer in the palette. Returns the repaired
 * grid plus a count of how many cells had to move, so the UI can be honest
 * about how much fidelity was lost.
 */
export function quantizeToAlpha(
  grid: ColorIndex[][],
  paletteHex: string[],
  forcedLeader?: ColorIndex,
): { baseColors: ColorIndex[]; leaderColor: ColorIndex; grid: ColorIndex[][]; changedCells: number } {
  const rowsN = grid.length;
  const cols = rowsN > 0 ? grid[0]!.length : 0;

  const tally = new Map<ColorIndex, number>();
  for (const row of grid) for (const c of row) tally.set(c, (tally.get(c) ?? 0) + 1);

  const leaderColor =
    forcedLeader ??
    [...tally.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ??
    0;

  const baseColors: ColorIndex[] = [];
  for (let c = 0; c < cols; c++) {
    const colTally = new Map<ColorIndex, number>();
    for (let r = 0; r < rowsN; r++) {
      const v = grid[r]![c]!;
      if (v !== leaderColor) colTally.set(v, (colTally.get(v) ?? 0) + 1);
    }
    const best = [...colTally.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0];
    // A column that is pure background still needs a base string to knot over;
    // give it the most common foreground colour in the whole picture.
    baseColors.push(
      best ??
        [...tally.entries()]
          .filter(([k]) => k !== leaderColor)
          .sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0] ??
        leaderColor,
    );
  }

  let changedCells = 0;
  const out = grid.map((row, _r) =>
    row.map((cell, c) => {
      const base = baseColors[c]!;
      if (cell === leaderColor || cell === base) return cell;
      changedCells++;
      const dLeader = colorDistance(paletteHex[cell], paletteHex[leaderColor]);
      const dBase = colorDistance(paletteHex[cell], paletteHex[base]);
      return dBase <= dLeader ? base : leaderColor;
    }),
  );

  return { baseColors, leaderColor, grid: out, changedCells };
}

function colorDistance(a: string | undefined, b: string | undefined): number {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  // Weighted RGB: cheap, dependency-free, and good enough to decide which of two
  // threads a stray pixel should snap to.
  const rMean = (ca[0] + cb[0]) / 2;
  const dr = ca[0] - cb[0];
  const dg = ca[1] - cb[1];
  const db = ca[2] - cb[2];
  return Math.sqrt(
    (2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db,
  );
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
